'use client';
import { clsx } from 'clsx';
import { useWindowSize } from 'react-use';
import { UA_THEME_VARS } from '@unifyapps/style/themes/consts';
import useScreen from '@unifyapps/hooks/useScreen';
import { useMemo } from 'react';
import BaseSnackbar from '../../_components/Snackbar';
import { Alert } from '../Alert';
import type { SnackbarProps } from './types';

const NON_MOBILE_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'right' } as const;
const MOBILE_ANCHOR_ORIGIN = { vertical: 'top', horizontal: 'center' } as const;

export function Snackbar(
  props: SnackbarProps & {
    open: boolean;
    onClose: () => void;
    style?: React.CSSProperties;
  },
) {
  const { width: windowWidth } = useWindowSize();
  const { isMobile } = useScreen();

  const {
    anchorOrigin,
    className,
    open,
    color,
    autoHideDuration,
    onClose,
    scheme = 'light',
    ...alertProps
  } = props;
  const anchor = anchorOrigin || (isMobile ? MOBILE_ANCHOR_ORIGIN : NON_MOBILE_ANCHOR_ORIGIN);

  const style = useMemo(() => {
    return {
      // NOTE: calculated using golden ratio of the screen width
      maxWidth: Math.max(windowWidth * 0.618 * 0.382, 500),
      ...props.style,
    };
  }, [props.style, windowWidth]);

  return (
    <BaseSnackbar
      anchorOrigin={anchor}
      autoHideDuration={autoHideDuration ?? (color === 'warning' ? 5000 : 3000)}
      className={clsx(
        'p-0',
        {
          'mt-topbarHeight': anchor.vertical === 'top', // NOTE: if the snackbar is show at top, we need to add margin to show snackbar below the topbar
        },
        className,
        UA_THEME_VARS,
      )}
      data-color-scheme={scheme}
      onClose={onClose}
      open={open}
      style={style}
    >
      <Alert
        className="h-full w-full"
        color={color}
        direction={alertProps.description || alertProps.bottomDecoratorNode ? 'col' : 'row'}
        onClose={onClose}
        {...alertProps}
      />
    </BaseSnackbar>
  );
}
